// API Configuration and Service
import { API_CONFIG } from '../config/api';
import { shouldBlockBot } from '../utils/botDetection';

class ApiService {
  constructor() {
    this.baseURL = API_CONFIG.BASE_URL;
    this.token = localStorage.getItem('auth_token');
  }

  // Get the base URL for API calls
  getBaseURL() {
    return this.baseURL;
  }

  // Get the full URL for an endpoint
  getFullURL(endpoint) {
    return `${this.baseURL}${endpoint}`;
  }

  // Set authentication token
  setToken(token) {
    this.token = token;
    if (token) {
      localStorage.setItem('auth_token', token);
    } else {
      localStorage.removeItem('auth_token');
    }
  }

  // Get authentication token
  getToken() {
    return this.token || localStorage.getItem('auth_token');
  }

  // Make HTTP request
  async request(endpoint, options = {}) {
    // Block bots before making any API requests - saves bandwidth
    if (shouldBlockBot()) {
      return Promise.reject({
        success: false,
        message: 'Access denied: Automated bots are not permitted',
        error: 'Bot detected on frontend - request blocked'
      });
    }

    const url = `${this.baseURL}${endpoint}`;
    const config = {
      ...options,
    };

    // Only set headers if not FormData
    if (!(options.body instanceof FormData)) {
      config.headers = {
        ...API_CONFIG.DEFAULT_HEADERS,
        ...options.headers,
      };
    } else {
      // For FormData, only set Authorization header
      config.headers = {
        ...options.headers,
      };
    }

    // Add authorization header if token exists
    const token = this.getToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }


    try {
      const response = await fetch(url, config);
      
      // Check if response is HTML (error page) instead of JSON
      const contentType = response.headers.get('content-type');
      if (contentType && !contentType.includes('application/json')) {
        const htmlText = await response.text();
        console.error('Received HTML instead of JSON:', htmlText.substring(0, 200));
        throw new Error(`Server returned HTML instead of JSON. Status: ${response.status}. Check if the API server is running and the endpoint exists.`);
      }
      
      const data = await response.json();

      if (!response.ok) {
        // Handle 401 Unauthorized - token expired or invalid
        if (response.status === 401) {
          console.log('401 Unauthorized - token expired or invalid');
          // Clear token and trigger logout
          this.setToken(null);
          // Clear localStorage token data
          localStorage.removeItem('auth_token');
          localStorage.removeItem('auth_user');
          localStorage.removeItem('auth_token_expiry');
          // DISABLED: Dispatch custom event for auth context to handle
          // window.dispatchEvent(new CustomEvent('auth:token-expired'));
        }
        
        // Create a custom error object that includes validation errors
        const error = new Error(data.message || `HTTP error! status: ${response.status}`);
        error.status = response.status;
        error.data = data;
        error.errors = data.errors || {};
        throw error;
      }

      return data;
    } catch (error) {
      console.error('API Request failed:', error);
      throw error;
    }
  }

  // GET request
  async get(endpoint, options = {}) {
    return this.request(endpoint, { ...options, method: 'GET' });
  }

  // POST request
  async post(endpoint, data, options = {}) {
    const config = {
      ...options,
      method: 'POST',
    };

    // Handle FormData (file uploads)
    if (data instanceof FormData) {
      config.body = data;
      // Don't set Content-Type header for FormData, let browser set it with boundary
    } else {
      config.body = JSON.stringify(data);
      config.headers = {
        'Content-Type': 'application/json',
        ...config.headers,
      };
    }

    return this.request(endpoint, config);
  }

  // PUT request
  async put(endpoint, data, options = {}) {
    return this.request(endpoint, {
      ...options,
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  // DELETE request
  async delete(endpoint, options = {}) {
    return this.request(endpoint, { ...options, method: 'DELETE' });
  }

  // File upload request
  async uploadFile(endpoint, formData, onProgress = null) {
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      
      if (onProgress) {
        xhr.upload.addEventListener('progress', onProgress);
      }
      
      xhr.addEventListener('load', () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          try {
            const response = JSON.parse(xhr.responseText);
            resolve(response);
          } catch (error) {
            reject(new Error('Invalid JSON response'));
          }
        } else {
          // Parse error response to extract validation messages
          try {
            const errorResponse = JSON.parse(xhr.responseText);
            // Create error object with response data for proper error handling
            const error = new Error(errorResponse.message || `Upload failed with status ${xhr.status}`);
            error.response = {
              status: xhr.status,
              data: errorResponse
            };
            reject(error);
          } catch (e) {
            // If response is not JSON, create error with status
            const error = new Error(`Upload failed with status ${xhr.status}`);
            error.response = {
              status: xhr.status,
              data: { message: `Upload failed with status ${xhr.status}` }
            };
            reject(error);
          }
        }
      });
      
      xhr.addEventListener('error', () => {
        const error = new Error('Upload failed');
        error.response = {
          status: 0,
          data: { message: 'Network error. Please check your connection.' }
        };
        reject(error);
      });
      
      xhr.open('POST', this.getFullURL(endpoint));
      xhr.setRequestHeader('Authorization', `Bearer ${this.getToken()}`);
      xhr.send(formData);
    });
  }

  // PATCH request
  async patch(endpoint, data, options = {}) {
    return this.request(endpoint, {
      ...options,
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  // PUT request with FormData
  async putFormData(endpoint, formData, options = {}) {
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      
      xhr.addEventListener('load', () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          try {
            const response = JSON.parse(xhr.responseText);
            resolve(response);
          } catch (error) {
            reject(new Error('Invalid JSON response'));
          }
        } else {
          try {
            const errorResponse = JSON.parse(xhr.responseText);
            reject(errorResponse);
          } catch (e) {
            reject(new Error(`Request failed with status ${xhr.status}`));
          }
        }
      });
      
      xhr.addEventListener('error', () => {
        reject(new Error('Request failed'));
      });
      
      xhr.open('PUT', this.getFullURL(endpoint));
      xhr.setRequestHeader('Authorization', `Bearer ${this.getToken()}`);
      xhr.send(formData);
    });
  }

  // PATCH request with FormData
  async patchFormData(endpoint, formData, options = {}) {
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      
      xhr.addEventListener('load', () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          try {
            const response = JSON.parse(xhr.responseText);
            resolve(response);
          } catch (error) {
            reject(new Error('Invalid JSON response'));
          }
        } else {
          try {
            const errorResponse = JSON.parse(xhr.responseText);
            reject(errorResponse);
          } catch (e) {
            reject(new Error(`Request failed with status ${xhr.status}`));
          }
        }
      });
      
      xhr.addEventListener('error', () => {
        reject(new Error('Request failed'));
      });
      
      xhr.open('PATCH', this.getFullURL(endpoint));
      xhr.setRequestHeader('Authorization', `Bearer ${this.getToken()}`);
      xhr.send(formData);
    });
  }
}

// Authentication API methods
export const authAPI = {
  // Register new user
  async register(userData) {
    const api = new ApiService();
    const response = await api.post('/auth/register', userData);
    
    // Store token if registration successful
    if (response.success && response.data.token) {
      api.setToken(response.data.token);
    }
    
    return response;
  },

  // Login user
  async login(credentials) {
    const api = new ApiService();
    const response = await api.post('/auth/login', credentials);
    
    // Store token if login successful
    if (response.success && response.data.token) {
      api.setToken(response.data.token);
    }
    
    return response;
  },

  // Sign in / sign up with a Firebase Google ID token
  async googleLogin(idToken) {
    const api = new ApiService();
    const response = await api.post('/auth/google', { id_token: idToken });

    // Store token if sign-in successful
    if (response.success && response.data.token) {
      api.setToken(response.data.token);
    }

    return response;
  },

  // Logout user
  async logout() {
    const api = new ApiService();
    const response = await api.post('/auth/logout');
    
    // Clear token
    api.setToken(null);
    
    return response;
  },

  // Get current user
  async getCurrentUser() {
    const api = new ApiService();
    return api.get('/auth/me');
  },

  // Refresh token
  async refreshToken() {
    const api = new ApiService();
    const response = await api.post('/auth/refresh');
    
    // Update token if refresh successful
    if (response.success && response.data.token) {
      api.setToken(response.data.token);
    }
    
    return response;
  },
};

// Categories API methods
export const categoriesAPI = {
  // Get all categories with optional filtering
  async getCategories(params = {}) {
    const api = new ApiService();
    const queryString = new URLSearchParams(params).toString();
    const endpoint = queryString ? `/categories?${queryString}` : '/categories';
    return api.get(endpoint);
  },

  // Get category tree structure
  async getCategoryTree() {
    const api = new ApiService();
    return api.get('/categories/tree');
  },

  // Get featured categories
  async getFeaturedCategories() {
    const api = new ApiService();
    return api.get('/categories/featured');
  },

  // Get root categories (categories with no parent)
  async getRootCategories() {
    const api = new ApiService();
    return api.get('/categories?parent_id=null');
  },

  // Get a specific category
  async getCategory(categoryId) {
    const api = new ApiService();
    return api.get(`/categories/${categoryId}`);
  },

  // Get children of a specific category
  async getCategoryChildren(categoryId) {
    const api = new ApiService();
    return api.get(`/categories/${categoryId}/children`);
  },

  // Get flat list of categories
  async getFlatCategories() {
    const api = new ApiService();
    return api.get('/categories/flat');
  },

  // Search categories
  async searchCategories(query) {
    const api = new ApiService();
    return api.get(`/categories/search?query=${encodeURIComponent(query)}`);
  },

  // Get categories by level
  async getCategoriesByLevel(level) {
    const api = new ApiService();
    return api.get(`/categories?level=${level}`);
  },

  // Get sub-categories of a parent category
  async getSubCategories(parentId) {
    const api = new ApiService();
    return api.get(`/categories?parent_id=${parentId}`);
  },
};

// Personal Projects API
export const personalProjectsAPI = {
  async getAll() {
    const api = new ApiService();
    return api.get('/personal-projects');
  },
  async create(data) {
    const api = new ApiService();
    return api.post('/personal-projects', data);
  },
  async getById(id) {
    const api = new ApiService();
    return api.get(`/personal-projects/${id}`);
  },
  async update(id, data) {
    const api = new ApiService();
    return api.put(`/personal-projects/${id}`, data);
  },
  async delete(id) {
    const api = new ApiService();
    return api.delete(`/personal-projects/${id}`);
  },
  async reorder(projects) {
    const api = new ApiService();
    return api.post('/personal-projects/reorder', { projects });
  },
  async uploadFile(formData, onProgress = null) {
    const api = new ApiService();
    return api.uploadFile('/upload-screenshot', formData, onProgress);
  },
};

// Forum API methods
export const forumAPI = {
  // Get all forums
  async getForums(params = {}) {
    const api = new ApiService();
    const queryString = new URLSearchParams(params).toString();
    const endpoint = queryString ? `/forums?${queryString}` : '/forums';
    return api.get(endpoint);
  },

  // Get a specific forum
  async getForum(forumId) {
    const api = new ApiService();
    return api.get(`/forums/${forumId}`);
  },

  // Create a new forum post
  async createForum(formData) {
    const api = new ApiService();
    return api.post('/forums', formData);
  },

  // Reply to a forum post (admin)
  async replyToForum(forumId, formData) {
    const api = new ApiService();
    return api.post(`/forums/${forumId}/reply`, formData);
  },

  // Reply to a forum post (public)
  async replyToForumPublic(forumId, formData) {
    const api = new ApiService();
    return api.post(`/forums/${forumId}/public-reply`, formData);
  },

  // Update forum status (admin only)
  async updateForumStatus(forumId, status) {
    const api = new ApiService();
    return api.patch(`/forums/${forumId}/status`, { status });
  },

  // Mark forum as notified (admin only)
  async markForumAsNotified(forumId) {
    const api = new ApiService();
    return api.patch(`/forums/${forumId}/notified`);
  },

  // Update a forum post
  async updateForum(forumId, formData) {
    const api = new ApiService();
    return api.patchFormData(`/forums/${forumId}`, formData);
  },

  // Delete a forum post
  async deleteForum(forumId) {
    const api = new ApiService();
    return api.delete(`/forums/${forumId}`);
  },

  // Delete an attachment from a forum post
  async deleteAttachment(forumId, attachmentId) {
    const api = new ApiService();
    return api.delete(`/forums/${forumId}/attachments/${attachmentId}`);
  },

  // Vote on a reply (value: 1 for thumbs up, -1 for thumbs down)
  async voteReply(replyId, value) {
    const api = new ApiService();
    return api.post(`/forum-replies/${replyId}/vote`, { value });
  },

  // Remove current user's vote from a reply
  async unvoteReply(replyId) {
    const api = new ApiService();
    return api.delete(`/forum-replies/${replyId}/vote`);
  },

  // Vote on a forum (value: 1 for thumbs up, -1 for thumbs down)
  async voteForum(forumId, value) {
    const api = new ApiService();
    return api.post(`/forums/${forumId}/vote`, { value });
  },

  // Remove current user's vote from a forum
  async unvoteForum(forumId) {
    const api = new ApiService();
    return api.delete(`/forums/${forumId}/vote`);
  }
};

// Settings API methods
export const settingsAPI = {
  async getByKey(key) {
    const api = new ApiService();
    return api.get(`/settings/${encodeURIComponent(key)}`);
  },

  // Check maintenance mode status (optimized for speed)
  async checkMaintenanceMode() {
    const api = new ApiService();
    // Use AbortController for faster cancellation if needed
    // Add cache busting timestamp to ensure fresh data
    return api.get(`/maintenance-mode?t=${Date.now()}`);
  }
};

// Admin API methods
export const adminAPI = {
  // Get all forums for admin
  async getForums(params = {}) {
    const api = new ApiService();
    const queryString = new URLSearchParams(params).toString();
    const endpoint = queryString ? `/admin/forums?${queryString}` : '/admin/forums';
    return api.get(endpoint);
  },

  // Get a specific forum for admin
  async getForum(forumId) {
    const api = new ApiService();
    return api.get(`/admin/forums/${forumId}`);
  },

  // Reply to a forum post as admin
  async replyToForum(forumId, formData) {
    const api = new ApiService();
    return api.post(`/admin/forums/${forumId}/reply`, formData);
  },

  // Update forum status (admin only)
  async updateForumStatus(forumId, status) {
    const api = new ApiService();
    return api.patch(`/admin/forums/${forumId}/status`, { status });
  },

  // Mark forum as notified (admin only)
  async markForumAsNotified(forumId) {
    const api = new ApiService();
    return api.patch(`/admin/forums/${forumId}/notified`);
  },

  // Toggle commenting enabled/disabled (admin only)
  async toggleCommenting(forumId, commentingEnabled) {
    const api = new ApiService();
    return api.patch(`/forums/${forumId}/commenting`, { commenting_enabled: commentingEnabled });
  },

  // Toggle voting enabled/disabled (admin only)
  async toggleVoting(forumId, votingEnabled) {
    const api = new ApiService();
    return api.patch(`/forums/${forumId}/voting`, { voting_enabled: votingEnabled });
  },

  // Get forum statistics
  async getStatistics() {
    const api = new ApiService();
    return api.get('/admin/forums-stats/statistics');
  }
};

// Blog API methods
export const blogAPI = {
  // Get all published blogs
  async getBlogs(params = {}) {
    const api = new ApiService();
    const queryString = new URLSearchParams(params).toString();
    const endpoint = queryString ? `/blogs?${queryString}` : '/blogs';
    return api.get(endpoint);
  },

  // Get a specific blog by slug
  async getBlog(slug) {
    const api = new ApiService();
    return api.get(`/blogs/${slug}`);
  },

  // Add a comment to a blog post (requires authentication)
  async addComment(slug, comment, parentId = null) {
    const api = new ApiService();
    return api.post(`/blogs/${slug}/comment`, { comment, parent_id: parentId });
  },

  // Like or dislike a blog post (requires authentication)
  async toggleLike(slug, type) {
    const api = new ApiService();
    return api.post(`/blogs/${slug}/like`, { type });
  }
};

// Test API connection
// Company API methods
export const companyAPI = {
  async getAll(params = {}) {
    const api = new ApiService();
    const queryString = new URLSearchParams(params).toString();
    const endpoint = queryString ? `/companies?${queryString}` : '/companies';
    return api.get(endpoint);
  },
  async get(id) {
    const api = new ApiService();
    return api.get(`/companies/${id}`);
  },
  async create(data) {
    const api = new ApiService();
    return api.post('/companies', data);
  },
  /** Create company + company user, switch to company mode. Returns user, company, company_profile. */
  async setup(data) {
    const api = new ApiService();
    return api.post('/companies/setup', data);
  },
  async update(id, data) {
    const api = new ApiService();
    return api.put(`/companies/${id}`, data);
  },
  async delete(id) {
    const api = new ApiService();
    return api.delete(`/companies/${id}`);
  },
  async follow(id) {
    const api = new ApiService();
    return api.post(`/companies/${id}/follow`);
  },
  async unfollow(id) {
    const api = new ApiService();
    return api.delete(`/companies/${id}/follow`);
  },
};

// Job API methods
export const jobAPI = {
  async getAll(params = {}) {
    const api = new ApiService();
    const queryString = new URLSearchParams(params).toString();
    const endpoint = queryString ? `/jobs?${queryString}` : '/jobs';
    return api.get(endpoint);
  },
  async get(id) {
    const api = new ApiService();
    return api.get(`/jobs/${id}`);
  },
  async create(data) {
    const api = new ApiService();
    return api.post('/jobs', data);
  },
  async update(id, data) {
    const api = new ApiService();
    return api.put(`/jobs/${id}`, data);
  },
  async updateStatus(id, status, isActive = null) {
    const api = new ApiService();
    const data = { status };
    if (isActive !== null) {
      data.is_active = isActive;
    }
    return api.put(`/jobs/${id}/status`, data);
  },
  async delete(id) {
    const api = new ApiService();
    return api.delete(`/jobs/${id}`);
  },
  async apply(id, data) {
    const api = new ApiService();
    return api.post(`/jobs/${id}/apply`, data);
  },
  async getApplications(id, params = {}) {
    const api = new ApiService();
    const queryString = new URLSearchParams(params).toString();
    const endpoint = queryString ? `/jobs/${id}/applications?${queryString}` : `/jobs/${id}/applications`;
    return api.get(endpoint);
  },
  async updateApplicationStatus(jobId, applicationId, status, notes = null, rejectionReason = null) {
    const api = new ApiService();
    const data = { status };
    if (notes !== null) {
      data.notes = notes;
    }
    if (rejectionReason !== null) {
      data.rejection_reason = rejectionReason;
    }
    return api.put(`/jobs/${jobId}/applications/${applicationId}`, data);
  },
  async updateApplicationNotes(jobId, applicationId, notes) {
    const api = new ApiService();
    return api.put(`/jobs/${jobId}/applications/${applicationId}`, { notes });
  },
  async getMyApplications(params = {}) {
    const api = new ApiService();
    const queryString = new URLSearchParams(params).toString();
    const endpoint = queryString ? `/my-applications?${queryString}` : '/my-applications';
    return api.get(endpoint);
  },
  async getShareLinks(id) {
    const api = new ApiService();
    return api.get(`/jobs/${id}/share-links`);
  },
  async report(id, data) {
    const api = new ApiService();
    return api.post(`/jobs/${id}/report`, data);
  },
  async unhide(id) {
    const api = new ApiService();
    return api.delete(`/jobs/${id}/hide`);
  },
  async getMyReports(params = {}) {
    const api = new ApiService();
    const queryString = new URLSearchParams(params).toString();
    const endpoint = queryString ? `/my-job-reports?${queryString}` : '/my-job-reports';
    return api.get(endpoint);
  },
  async getMyActivities(params = {}) {
    const api = new ApiService();
    const queryString = new URLSearchParams(params).toString();
    const endpoint = queryString ? `/my-activities?${queryString}` : '/my-activities';
    return api.get(endpoint);
  },
  async getStats() {
    const api = new ApiService();
    return api.get('/jobs/stats');
  },
};

// Resume API methods
export const resumeAPI = {
  async getAll() {
    const api = new ApiService();
    return api.get('/user/resumes');
  },
  async get(id) {
    const api = new ApiService();
    return api.get(`/user/resumes/${id}`);
  },
  async getDefault() {
    const api = new ApiService();
    return api.get('/user/resume/default');
  },
  async create(formData) {
    const api = new ApiService();
    return api.postFormData('/user/resumes', formData);
  },
  async setDefault(id) {
    const api = new ApiService();
    return api.put(`/user/resumes/${id}/set-default`);
  },
  async delete(id) {
    const api = new ApiService();
    return api.delete(`/user/resumes/${id}`);
  },
};

// Feed API methods
export const feedAPI = {
  async getFeed(type = 'all', params = {}) {
    const api = new ApiService();
    const endpoint = type === 'all' ? '/feed' : `/feed/${type}`;
    const queryString = new URLSearchParams(params).toString();
    const fullEndpoint = queryString ? `${endpoint}?${queryString}` : endpoint;
    return api.get(fullEndpoint);
  },
  async getCompaniesFeed(params = {}) {
    const api = new ApiService();
    return feedAPI.getFeed('companies', params);
  },
  async getUsersFeed(params = {}) {
    const api = new ApiService();
    return feedAPI.getFeed('users', params);
  },
  async getJobsFeed(params = {}) {
    const api = new ApiService();
    return feedAPI.getFeed('jobs', params);
  },
};

// User Following API methods
export const userFollowAPI = {
  async follow(userId) {
    const api = new ApiService();
    return api.post(`/users/${userId}/follow`);
  },
  async unfollow(userId) {
    const api = new ApiService();
    return api.delete(`/users/${userId}/follow`);
  },
  async getFollowers(userId, params = {}) {
    const api = new ApiService();
    const queryString = new URLSearchParams(params).toString();
    const endpoint = queryString ? `/users/${userId}/followers?${queryString}` : `/users/${userId}/followers`;
    return api.get(endpoint);
  },
  async getFollowing(userId, params = {}) {
    const api = new ApiService();
    const queryString = new URLSearchParams(params).toString();
    const endpoint = queryString ? `/users/${userId}/following?${queryString}` : `/users/${userId}/following`;
    return api.get(endpoint);
  },
  async getFollowStatus(userId) {
    const api = new ApiService();
    return api.get(`/users/${userId}/follow-status`);
  },
};

// Notification API methods
export const notificationAPI = {
  async getAll(params = {}) {
    const api = new ApiService();
    const queryString = new URLSearchParams(params).toString();
    const endpoint = queryString ? `/notifications?${queryString}` : '/notifications';
    return api.get(endpoint);
  },
  async getUnreadCount() {
    const api = new ApiService();
    return api.get('/notifications/unread-count');
  },
  async markAsRead(notificationId) {
    const api = new ApiService();
    return api.post(`/notifications/${notificationId}/read`);
  },
  async markAllAsRead() {
    const api = new ApiService();
    return api.post('/notifications/mark-all-read');
  },
};

// Talent/Profile Search API methods
export const talentAPI = {
  async search(params = {}) {
    const api = new ApiService();
    const queryString = new URLSearchParams(params).toString();
    const endpoint = queryString ? `/talent?${queryString}` : '/talent';
    return api.get(endpoint);
  },
  async getBySlug(slug) {
    const api = new ApiService();
    return api.get(`/talent/${slug}`);
  }
};

// Bookmark API methods
export const bookmarkAPI = {
  async getLists(type = 'all') {
    const api = new ApiService();
    const params = type !== 'all' ? `?type=${type}` : '';
    return api.get(`/bookmarks/lists${params}`);
  },
  async createList(data) {
    const api = new ApiService();
    return api.post('/bookmarks/lists', data);
  },
  async updateList(id, data) {
    const api = new ApiService();
    return api.put(`/bookmarks/lists/${id}`, data);
  },
  async deleteList(id) {
    const api = new ApiService();
    return api.delete(`/bookmarks/lists/${id}`);
  },
  async addTalent(data) {
    const api = new ApiService();
    return api.post('/bookmarks/add', data);
  },
  async removeTalent(data) {
    const api = new ApiService();
    return api.post('/bookmarks/remove', data);
  },
  async checkBookmarked(talentId) {
    const api = new ApiService();
    return api.get(`/bookmarks/check/${talentId}`);
  },
  async getBookmarkedTalents(params = {}) {
    const api = new ApiService();
    const queryString = new URLSearchParams(params).toString();
    const endpoint = queryString ? `/bookmarks/talents?${queryString}` : '/bookmarks/talents';
    return api.get(endpoint);
  },
  // Vacancy (job) bookmark methods
  async addVacancy(data) {
    const api = new ApiService();
    return api.post('/bookmarks/add-vacancy', data);
  },
  async removeVacancy(data) {
    const api = new ApiService();
    return api.post('/bookmarks/remove-vacancy', data);
  },
  async checkVacancyBookmarked(jobId) {
    const api = new ApiService();
    return api.get(`/bookmarks/check-vacancy/${jobId}`);
  },
  async getBookmarkedVacancies(params = {}) {
    const api = new ApiService();
    const queryString = new URLSearchParams(params).toString();
    const endpoint = queryString ? `/bookmarks/vacancies?${queryString}` : '/bookmarks/vacancies';
    return api.get(endpoint);
  }
};

// Talent Search Preferences API methods
export const talentSearchPreferenceAPI = {
  async getAll() {
    const api = new ApiService();
    return api.get('/talent-search-preferences');
  },
  async create(data) {
    const api = new ApiService();
    return api.post('/talent-search-preferences', data);
  },
  async update(id, data) {
    const api = new ApiService();
    return api.put(`/talent-search-preferences/${id}`, data);
  },
  async delete(id) {
    const api = new ApiService();
    return api.delete(`/talent-search-preferences/${id}`);
  },
  async toggleActive(id) {
    const api = new ApiService();
    return api.post(`/talent-search-preferences/${id}/toggle-active`);
  }
};

// Job Search Preferences API methods
export const jobSearchPreferenceAPI = {
  async getAll() {
    const api = new ApiService();
    return api.get('/job-search-preferences');
  },
  async create(data) {
    const api = new ApiService();
    return api.post('/job-search-preferences', data);
  },
  async update(id, data) {
    const api = new ApiService();
    return api.put(`/job-search-preferences/${id}`, data);
  },
  async delete(id) {
    const api = new ApiService();
    return api.delete(`/job-search-preferences/${id}`);
  },
  async toggleActive(id) {
    const api = new ApiService();
    return api.post(`/job-search-preferences/${id}/toggle-active`);
  }
};

// User Report API methods
export const userReportAPI = {
  async report(data) {
    const api = new ApiService();
    return api.post('/user-reports', data);
  },
};

// User Hide/Unhide API methods
export const userHideAPI = {
  async hide(userId) {
    const api = new ApiService();
    return api.post(`/users/${userId}/hide`);
  },
  async unhide(userId) {
    const api = new ApiService();
    return api.delete(`/users/${userId}/hide`);
  },
};

// User Reviews API methods
export const userReviewAPI = {
  async getReviews(userId, params = {}) {
    const api = new ApiService();
    const queryString = new URLSearchParams(params).toString();
    const endpoint = queryString ? `/users/${userId}/reviews?${queryString}` : `/users/${userId}/reviews`;
    return api.get(endpoint);
  },
  async getMyReview(userId) {
    const api = new ApiService();
    return api.get(`/users/${userId}/reviews/my-review`);
  },
  async createReview(data) {
    const api = new ApiService();
    return api.post('/user-reviews', data);
  },
  async updateReview(reviewId, data) {
    const api = new ApiService();
    return api.put(`/user-reviews/${reviewId}`, data);
  },
  async deleteReview(reviewId) {
    const api = new ApiService();
    return api.delete(`/user-reviews/${reviewId}`);
  },
};

// Account API methods
export const accountAPI = {
  async deleteAccount(password, confirmation) {
    const api = new ApiService();
    return api.delete('/profile/delete-account', {
      body: JSON.stringify({ password, confirmation }),
      headers: {
        'Content-Type': 'application/json'
      }
    });
  },
};

// Activity API methods
export const activityAPI = {
  async createUserActivity(data) {
    const api = new ApiService();
    return api.post('/user-activities', data);
  },
  async createCompanyActivity(data) {
    const api = new ApiService();
    return api.post('/company-activities', data);
  },
  async likeUserActivity(activityId) {
    const api = new ApiService();
    return api.post(`/user-activities/${activityId}/like`);
  },
  async unlikeUserActivity(activityId) {
    const api = new ApiService();
    return api.delete(`/user-activities/${activityId}/like`);
  },
  async likeCompanyActivity(activityId) {
    const api = new ApiService();
    return api.post(`/company-activities/${activityId}/like`);
  },
  async unlikeCompanyActivity(activityId) {
    const api = new ApiService();
    return api.delete(`/company-activities/${activityId}/like`);
  },
};

// Mode switching API
export const modeAPI = {
  async switchMode(mode) {
    const api = new ApiService();
    return api.post('/user/switch-mode', { mode });
  },
  async getCurrentMode() {
    const api = new ApiService();
    return api.get('/user/current-mode');
  },
  async getCompanies() {
    const api = new ApiService();
    return api.get('/user/companies');
  },
  async getCompanyProfiles() {
    const api = new ApiService();
    return api.get('/user/company-profiles');
  },
  async getCompanyProfile(id) {
    const api = new ApiService();
    return api.get(`/user/company-profiles/${id}`);
  },
  async updateCompanyProfile(id, data) {
    const api = new ApiService();
    return api.put(`/user/company-profiles/${id}`, data);
  },
  async closeCompany(id, data = {}) {
    const api = new ApiService();
    return api.post(`/user/company-profiles/${id}/close`, data);
  },
  async openCompany(id) {
    const api = new ApiService();
    return api.post(`/user/company-profiles/${id}/open`);
  },
};

export const testAPI = {
  async test() {
    const api = new ApiService();
    return api.get('/test');
  },
};

export default new ApiService();
